<script setup>
import FixtureCell from '~/components/fixtures/FixtureCell.vue';
import TeamCard from '~/components/teams/TeamCard.vue';
import {computed} from 'vue';
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";

const props = defineProps({
  stage: {
    type: Object,
    required: true
  },
  round: {
    type: Number,
    required: true
  },
  totalRounds: {
    type: Number,
    required: true
  }
});

// Get fixtures and BYE teams combined for display
const displayItems = computed(() => {
  const items = [];
  const fixtures = props.stage.fixtures || [];
  const teams = props.stage.teams || [];

  // Get BYE teams (auto-advancing)
  const byeTeams = teams.filter(t => t.isBye).sort((a, b) => a.bracketPosition - b.bracketPosition);

  // Sort fixtures by bracket position if available, otherwise by creation
  const sortedFixtures = [...fixtures].sort((a, b) => {
    // Try to sort by home team bracket position
    const aPos = teams.find(t => t.id === a.homeTeamId)?.bracketPosition || 0;
    const bPos = teams.find(t => t.id === b.homeTeamId)?.bracketPosition || 0;
    return aPos - bPos;
  });

  // Interleave BYE teams and fixtures based on bracket positions
  let fixtureIndex = 0;
  let byeIndex = 0;
  let position = 1;

  while (fixtureIndex < sortedFixtures.length || byeIndex < byeTeams.length) {
    const fixture = sortedFixtures[fixtureIndex];
    const byeTeam = byeTeams[byeIndex];

    const fixturePos = fixture ? (teams.find(t => t.id === fixture.homeTeamId)?.bracketPosition || position) : Infinity;
    const byePos = byeTeam ? byeTeam.bracketPosition : Infinity;

    if (byePos < fixturePos) {
      items.push({
        type: 'bye',
        team: byeTeam,
        position: byePos
      });
      byeIndex++;
    } else if (fixture) {
      items.push({
        type: 'fixture',
        fixture: fixture,
        position: fixturePos
      });
      fixtureIndex++;
    }

    position++;
  }

  return items;
});
</script>

<template>
  <div class="d-flex flex-column" style="min-width: 300px;">
    <h4 class="stage-label mb-2">{{ stage.name }}</h4>
    <div class="d-flex flex-column">
      <div
          v-for="item in displayItems"
          :key="item.type === 'fixture' ? item.fixture.id : item.team.id"
          class="position-relative mb-4"
          :class="{ 'bracket-match-card': item.type === 'fixture' }"
      >
        <fixture-result-row
            class="mb-0"
            v-if="item.type === 'fixture'"
            :fixture="item.fixture"
            size="small"
            :allow-editing="true"
        />
        <div
            v-else
            class="bracket-bye"
        >
          <div class="d-flex align-center justify-center ga-2 pa-4">
            <team-card :team="item.team" size="small" :expandable="false"/>
            <span class="tl-chip tl-chip--ok">BYE</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.stage-label {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.bracket-match-card {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.bracket-bye {
  border: 1.5px dashed rgb(var(--v-theme-success));
  border-radius: 12px;
  background: rgb(var(--v-theme-successTint));
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
